import { useCallback, useEffect, useState } from 'react'
import { Flex, InputDescriptionWrap } from 'components'
import { useFormikContext } from 'formik'
import {
  getTypeFromGeocodeAPI,
  IAddressComponent,
  IDataDestinations,
  isNumber,
  IVenueType,
} from 'utils'
import { Input } from 'components/Input'
import { IVenueFormValues } from './FormPage'
import Geocode from 'react-geocode'
import { Map } from 'components'
import { gql, useQuery } from '@apollo/client'
import { getDistance } from 'geolib'
import { rem } from 'polished'
import styled from '@emotion/styled'
import debounce from 'lodash.debounce'
import { inputDescriptions } from './inputDescriptions'
import { SelectValue } from '@nextretreat/ui-components/dist/Select'
import { GeolibInputCoordinates } from 'geolib/es/types'
import CountrySelect from 'components/CountryInput/CountryInput'

interface IStepProps {
  venueTypes?: IVenueType[]
}

const GET_DESTINATIONS = gql`
  query AvailableDestinations {
    availableDestinations {
      id
      title
      countryCode
      coordinates {
        lat
        lon
      }
    }
  }
`

export const StyledFlex = styled(Flex)`
  gap: ${rem(10)};

  & > * {
    flex: 1;
    flex-basis: 100%;
  }
`

export const EmptyDiv = styled.div`
  flex-grow: 1;
  flex-basis: 100%;
  box-sizing: border-box;
  padding-right: ${rem(1)};
`

export const GeneralInfoStep = ({ venueTypes }: IStepProps) => {
  const { setFieldValue, values } = useFormikContext<IVenueFormValues>()

  const [destinations, setDestinations] = useState<
    { label: string; value: string | number }[]
  >([])
  const [manually, setManually] = useState<boolean>(false)

  const { error, data } = useQuery<IDataDestinations>(GET_DESTINATIONS)

  if (error) console.error(error)

  useEffect(() => {
    if (!values.generalInfo.address && values.generalInfo.coordinates)
      setFieldValue('generalInfo.coordinates', undefined)

    const debouncedThing = debounce(() => {
      if (values.generalInfo.address) {
        Geocode.fromAddress(values.generalInfo.address).then(
          (response) => {
            const country = getTypeFromGeocodeAPI(
              response.results[0].address_components,
              ['country']
            )
            const locality = getTypeFromGeocodeAPI(
              response.results[0].address_components,
              [
                'locality',
                'administrative_area_level_3',
                'administrative_area_level_1',
              ]
            )

            setFieldValue(
              'generalInfo.city',
              (locality as IAddressComponent)?.long_name || ''
            )
            if (country) {
              setFieldValue('generalInfo.country', country.long_name)
              setFieldValue('generalInfo.countryCode', country.short_name)
            }
            const { lat, lng } = response.results[0].geometry.location
            setFieldValue('generalInfo.coordinates', { lat, lng })
          },
          () => {
            setFieldValue('generalInfo.coordinates', undefined)
          }
        )
      }
    }, 500)

    debouncedThing()

    return () => debouncedThing.cancel()
  }, [values.generalInfo.address])

  useEffect(() => {
    if (
      values.generalInfo.coordinates &&
      values.generalInfo.address &&
      data &&
      !manually
    ) {
      const distanceArray = data?.availableDestinations.map(
        ({ coordinates: destinationCoords, ...obj }) => ({
          ...obj,
          distance:
            getDistance(
              destinationCoords,
              values.generalInfo.coordinates as GeolibInputCoordinates
            ) / 1000,
        })
      )

      distanceArray.sort((a, b) => a.distance - b.distance)

      if (
        distanceArray[0].distance < 200 &&
        distanceArray[0].countryCode === values.generalInfo.countryCode
      ) {
        setFieldValue('generalInfo.destination', {
          label: distanceArray[0].title,
          value: distanceArray[0].id,
        })
      } else if (values.generalInfo.city)
        setFieldValue('generalInfo.destination', {
          label: values.generalInfo.city,
          value: `COMMON`,
        })
    }
  }, [values.generalInfo.coordinates])

  useEffect(() => {
    if (!destinations.length && data) {
      setDestinations(
        (data.availableDestinations || []).map(
          ({ id: value, title: label }) => ({
            label,
            value,
          })
        )
      )
    }
  }, [data?.availableDestinations])

  const onCreateOption = useCallback((value: string) => {
    const id = `custom_${value}`

    setDestinations((prev) => [{ value: id, label: value }, ...prev])
    onSetDestinationValue({ value: id, label: value })
  }, [])

  const onSetDestinationValue = useCallback(
    (val: { label: string; value: string }) => {
      setManually(true)
      setFieldValue('generalInfo.destination', val)
    },
    []
  )

  const coords = values.generalInfo.coordinates

  const hasCoords = isNumber(coords?.lat) && isNumber(coords?.lng)

  return (
    <>
      <InputDescriptionWrap text={inputDescriptions.generalInfo.name}>
        <Input
          isBlock
          name="generalInfo.name"
          label="Title"
          placeholder="Enter title"
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.description}>
        <Input.Textarea
          isBlock
          name="generalInfo.description"
          label="Description"
          placeholder="Enter description"
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.checkIns}>
        <StyledFlex>
          <Input.TimePicker name="generalInfo.checkIn" label="Check-in" />

          <Input.TimePicker name="generalInfo.checkOut" label="Check-out" />
        </StyledFlex>
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.address}>
        <Input
          isBlock
          name="generalInfo.address"
          label="Address"
          placeholder="Enter venue's address"
        />
      </InputDescriptionWrap>

      {hasCoords ? (
        <InputDescriptionWrap text={inputDescriptions.generalInfo.coordinates}>
          <Map
            coordinates={{ lat: Number(coords.lat), lng: Number(coords.lng) }}
          />
        </InputDescriptionWrap>
      ) : null}

      {hasCoords && (
        <InputDescriptionWrap text={inputDescriptions.generalInfo.coordinates}>
          <StyledFlex>
            <Input
              type="number"
              label="Latitude"
              placeholder="Enter latitude"
              name="generalInfo.coordinates.lat"
            />

            <Input
              type="number"
              label="Longitude"
              placeholder="Enter longitude"
              name="generalInfo.coordinates.lng"
            />
          </StyledFlex>
        </InputDescriptionWrap>
      )}

      <InputDescriptionWrap text={inputDescriptions.generalInfo.city}>
        <Input
          isBlock
          name="generalInfo.city"
          label="City"
          placeholder="Enter venue's city"
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.country}>
        <CountrySelect />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.destination}>
        <Input.Select
          isBlock
          label="Destination"
          name="generalInfo.destination"
          onChange={(val) => onSetDestinationValue(val)}
          placeholder="Select destination"
          value={values.generalInfo.destination as SelectValue}
          options={destinations}
          onCreateOption={onCreateOption}
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.venueType}>
        <Input.Select
          isBlock
          label="Venue Type"
          name="generalInfo.venueType"
          placeholder="Select venue type"
          onChange={(val) => setFieldValue('generalInfo.venueType', val)}
          value={values.generalInfo.venueType as SelectValue}
          options={(venueTypes || []).map(({ key: label, value }) => ({
            label,
            value,
          }))}
        />
      </InputDescriptionWrap>

      <InputDescriptionWrap text={inputDescriptions.generalInfo.isEntireHouse}>
        <Input.Checkbox
          checked={values.generalInfo.isEntireHouse}
          label="I’m offering to rent an entire house/villa (full-buyout)"
          name="generalInfo.isEntireHouse"
          onChange={(val) => setFieldValue('generalInfo.isEntireHouse', val)}
        />
      </InputDescriptionWrap>

      {values.generalInfo.isEntireHouse && (
        <InputDescriptionWrap text={inputDescriptions.generalInfo.price}>
          <Input
            isBlock
            type="number"
            suffix="€"
            label="Price per night"
            placeholder="Enter price per night in €"
            min={0}
            name="generalInfo.price"
          />
        </InputDescriptionWrap>
      )}

      <InputDescriptionWrap text={inputDescriptions.generalInfo.ical}>
        <Input
          isBlock
          label="Ical Link"
          placeholder="Enter ical link"
          name="generalInfo.ical"
        />
      </InputDescriptionWrap>
    </>
  )
}
